import { useState } from 'react';
import { YStack, ScrollView } from 'tamagui';
import { useAuth } from '../../auth/useAuth';
import '../Login/auth-form.css';

interface SignupProps {
  onSignup?: (name: string, email: string, password: string) => void;
  onNavigateToLogin?: () => void;
}

export const Signup = ({ onSignup, onNavigateToLogin }: SignupProps) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const { signup, isLoading } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!name || !email || !password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    try {
      if (onSignup) {
        await onSignup(name, email, password);
      } else {
        await signup(email, password, name);
        setSuccess('Account created successfully! Please check your email to verify your account.');
        setName('');
        setEmail('');
        setPassword('');
        setConfirmPassword('');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Signup failed. Please try again.';
      setError(errorMessage);
    }
  };

  return (
    <ScrollView flex={1} backgroundColor="$background">
      <YStack className="auth-container">
        <div className="auth-wrapper">
          <div className="auth-card">
            {/* Header */}
            <div className="auth-header">
              <h1 className="auth-title">Create Account</h1>
              <p className="auth-subtitle">Sign up to get started with TaskMaster</p>
            </div>

            {/* Alert Messages */}
            {success && (
              <div className="auth-alert auth-alert-success">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M17 8L8.5 16.5L3 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>{success}</span>
              </div>
            )}

            {error && (
              <div className="auth-alert auth-alert-error">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="2"/>
                  <path d="M10 6V10M10 14H10.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <span>{error}</span>
              </div>
            )}

            {/* Form */}
            <form className="auth-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name" className="form-label">
                  Full Name
                </label>
                <div className={`form-input-wrapper ${focusedField === 'name' ? 'focused' : ''}`}>
                  <svg className="form-input-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M10 10C12.2091 10 14 8.20914 14 6C14 3.79086 12.2091 2 10 2C7.79086 2 6 3.79086 6 6C6 8.20914 7.79086 10 10 10ZM3 18C3.5304 16.3957 4.60541 15.0593 6 14.2907C7.39459 13.5221 9.06935 13.2 10.5 13.2H11.5C13.9118 13.2 16.2353 14.0736 17.8995 15.6005C18.6167 16.2829 19.2624 17.0347 19.8 17.8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <input
                    id="name"
                    type="text"
                    placeholder="John Doe"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onFocus={() => setFocusedField('name')}
                    onBlur={() => setFocusedField(null)}
                    className="form-input"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  Email Address
                </label>
                <div className={`form-input-wrapper ${focusedField === 'email' ? 'focused' : ''}`}>
                  <svg className="form-input-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M2 4L9 10L16 4M2 4H16C17.1046 4 18 4.89543 18 6V14C18 15.1046 17.1046 16 16 16H4C2.89543 16 2 15.1046 2 14V4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setFocusedField('email')}
                    onBlur={() => setFocusedField(null)}
                    className="form-input"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="password" className="form-label">
                  Password
                </label>
                <div className={`form-input-wrapper ${focusedField === 'password' ? 'focused' : ''}`}>
                  <svg className="form-input-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M5 9V7C5 4.24 7.24 2 10 2C12.76 2 15 4.24 15 7V9C15.83 9 16.5 9.67 16.5 10.5V18C16.5 19.38 15.38 20.5 14 20.5H6C4.62 20.5 3.5 19.38 3.5 18V10.5C3.5 9.67 4.17 9 5 9ZM7 9H13V7C13 5.34 11.66 4 10 4C8.34 4 7 5.34 7 7V9Z" fill="currentColor"/>
                  </svg>
                  <input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setFocusedField('password')}
                    onBlur={() => setFocusedField(null)}
                    className="form-input"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword" className="form-label">
                  Confirm Password
                </label>
                <div className={`form-input-wrapper ${focusedField === 'confirmPassword' ? 'focused' : ''}`}>
                  <svg className="form-input-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M5 9V7C5 4.24 7.24 2 10 2C12.76 2 15 4.24 15 7V9C15.83 9 16.5 9.67 16.5 10.5V18C16.5 19.38 15.38 20.5 14 20.5H6C4.62 20.5 3.5 19.38 3.5 18V10.5C3.5 9.67 4.17 9 5 9ZM7 9H13V7C13 5.34 11.66 4 10 4C8.34 4 7 5.34 7 7V9Z" fill="currentColor"/>
                  </svg>
                  <input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onFocus={() => setFocusedField('confirmPassword')}
                    onBlur={() => setFocusedField(null)}
                    className="form-input"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <button
                type="submit"
                className="btn-primary-submit"
                disabled={isLoading || !name || !email || !password || !confirmPassword}
              >
                {isLoading ? 'Creating Account...' : 'Create Account'}
              </button>
            </form>

            {/* Footer */}
            <div className="auth-footer">
              <p className="auth-footer-text">
                Already have an account?{' '}
                <button 
                  type="button"
                  className="auth-link"
                  onClick={onNavigateToLogin}
                >
                  Sign In
                </button>
              </p>
            </div>
          </div>

          {/* Side Decoration */}
          <div className="auth-decoration auth-decoration-1"></div>
          <div className="auth-decoration auth-decoration-2"></div>
        </div>
      </YStack>
    </ScrollView>
  );
};
